import { getCurrentLocale } from '@/lib/ai/i18n'

export const FULL = 'full'
export const DATE_SHORT = 'dateShort'
export const DATE_LONG = 'dateLong'
export const ISO_DATE = 'dateISO'
export const NAMED_MONTH_WITH_YEAR = 'namedMonthWithYear'
export const TIME = 'time'
export const NAMED_WEEKDAY_WITH_YEAR_WITH_TIME = 'namedWeekdayWithYearWithTime'
export const NAMED_WEEKDAY_WITH_YEAR = 'namedWeekdayWithYear'
export const TIME_ONLY = 'timeOnly'

export type DateVariant =
  | typeof FULL
  | typeof DATE_SHORT
  | typeof DATE_LONG
  | typeof ISO_DATE
  | typeof NAMED_MONTH_WITH_YEAR
  | typeof TIME
  | typeof TIME_ONLY
  | typeof NAMED_WEEKDAY_WITH_YEAR_WITH_TIME
  | typeof NAMED_WEEKDAY_WITH_YEAR

export function formatDate(
  dateString: string | Date,
  variant: DateVariant = FULL,
  locale: string = getCurrentLocale()
): string {
  if (!dateString) return ''

  // 'YYYY-MM-DD' would parse as UTC midnight and show the previous day west of UTC (e.g. US);
  // treat it as a local calendar date instead. Date objects and timestamps stay unchanged.
  const isDateOnly = typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)
  const date = isDateOnly ? new Date(`${dateString}T00:00`) : new Date(dateString)
  if (isNaN(date.getTime())) return ''

  if (variant === ISO_DATE) {
    if (isDateOnly) return dateString as string
    return date.toISOString().slice(0, 10) // 'YYYY-MM-DD'
  }

  if (variant === TIME_ONLY) {
    return new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date)
  }

  const is = (...variants: string[]) => variants.includes(variant)

  return new Intl.DateTimeFormat(locale, {
    year: is(
      FULL,
      DATE_LONG,
      NAMED_MONTH_WITH_YEAR,
      NAMED_WEEKDAY_WITH_YEAR_WITH_TIME,
      NAMED_WEEKDAY_WITH_YEAR
    )
      ? 'numeric'
      : '2-digit',
    month: is(NAMED_MONTH_WITH_YEAR) ? 'long' : '2-digit',
    weekday: is(NAMED_WEEKDAY_WITH_YEAR_WITH_TIME, NAMED_WEEKDAY_WITH_YEAR) ? 'long' : undefined,
    ...(!is(NAMED_MONTH_WITH_YEAR) && { day: '2-digit' }),
    ...(is(FULL, TIME, NAMED_WEEKDAY_WITH_YEAR_WITH_TIME) && {
      hour: '2-digit',
      minute: '2-digit',
    }),
  }).format(date)
}

/** Vuetify v-time-picker format matching the locale's clock, e.g. 'ampm' for en-US. */
export function timePickerFormat(locale: string = getCurrentLocale()): 'ampm' | '24hr' {
  const { hourCycle } = new Intl.DateTimeFormat(locale, { hour: 'numeric' }).resolvedOptions()
  return hourCycle === 'h11' || hourCycle === 'h12' ? 'ampm' : '24hr'
}

export function getTime(dateString: string | Date, locale: string = getCurrentLocale()): string {
  if (!dateString) return ''

  const date = new Date(dateString)
  if (isNaN(date.getTime())) return ''

  try {
    return date.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch (error) {
    console.error('Invalid locale in getTime:', locale, error)
    // Fallback to default locale
    return date.toLocaleTimeString('de', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }
}
